import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { returnsAPI, customerAPI } from '../../services/api';

const ReturnsReport = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [returns, setReturns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [customerId, setCustomerId] = useState('');
  const [statusFilter, setStatusFilter] = useState('Processed');

  useEffect(() => {
    customerAPI.getAll().then(res => {
      setCustomers(res.data.data || []);
    });
  }, []);

  useEffect(() => {
    loadReturns();
  }, [startDate, endDate, customerId, statusFilter]);

  const loadReturns = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: startDate,
        end_date: endDate,
        status: statusFilter,
      };
      if (customerId) params.customer_id = customerId;

      const response = await returnsAPI.getAll(params);
      setReturns(response.data.data || []);
    } catch (error) {
      console.error('Error loading returns:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = () => {
    return {
      total_returns: returns.length,
      total_refunded: returns.reduce((sum, r) => sum + (r.total_refund || 0), 0),
      total_vat_reversed: returns.reduce((sum, r) => sum + (r.vat_refund || 0), 0),
      total_items_refunded: returns.reduce((sum, r) => sum + (r.items_refund || 0), 0),
    };
  };

  const summary = calculateSummary();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900">
          {t('returns.title')}
        </h2>
        <p className="text-gray-600 mt-1">{t('returns.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('common.startDate')}
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('common.endDate')}
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('salesOrders.customer')}
            </label>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">{t('common.all')}</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('common.status')}
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">{t('common.all')}</option>
              <option value="Processed">{t('returns.statusProcessed')}</option>
              <option value="Pending">{t('returns.statusPending')}</option>
              <option value="Approved">{t('returns.statusApproved')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-red-50 rounded-lg p-6">
          <p className="text-sm font-medium text-red-600">Total Returns</p>
          <p className="text-4xl font-bold text-red-900 mt-2">{summary.total_returns}</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-6">
          <p className="text-sm font-medium text-orange-600">Total Refunded</p>
          <p className="text-2xl font-bold text-orange-900 mt-2">
            {t('common.currency')} {summary.total_refunded.toFixed(2)}
          </p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-6">
          <p className="text-sm font-medium text-yellow-600">VAT Reversed</p>
          <p className="text-2xl font-bold text-yellow-900 mt-2">
            {t('common.currency')} {summary.total_vat_reversed.toFixed(2)}
          </p>
        </div>
        <div className="bg-blue-50 rounded-lg p-6">
          <p className="text-sm font-medium text-blue-600">Items Value</p>
          <p className="text-2xl font-bold text-blue-900 mt-2">
            {t('common.currency')} {summary.total_items_refunded.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Details Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {t('returns.returnNumber')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {t('salesOrders.orderNumber')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {t('salesOrders.customer')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {t('returns.reason')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {t('returns.itemsRefund')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {t('returns.vatRefund')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {t('returns.totalRefund')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {returns.length > 0 ? (
                returns.map(ret => (
                  <tr key={ret.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600">
                      {ret.return_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                      {ret.sales_order?.order_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {ret.sales_order?.customer?.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {ret.return_reason}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-orange-600">
                      {t('common.currency')} {ret.items_refund?.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-yellow-600">
                      {t('common.currency')} {ret.vat_refund?.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-red-700">
                      {t('common.currency')} {ret.total_refund?.toFixed(2)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    {t('common.noData')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReturnsReport;
